import { type JSX, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { cn } from '../lib/utils.ts';
import type { NavContext } from '../lib/use-nav-context.ts';
import { sectionHeadings } from '../lib/nav-sections.ts';

/**
 * Renders a navigation model (back-links, items, sections, sub-links, and a pinned
 * block carrying Help and the privacy notice) for whichever surface hosts it - the desktop rail (AppSidebar) or the
 * mobile drawer (MobileNavDrawer). Both pass the same `useNavContext()` result,
 * so the two can never present different destinations.
 *
 * `idSuffix` keeps data-testids unique across surfaces: the rail is always
 * mounted (it is `hidden … lg:flex`, not unmounted), so an open drawer would
 * otherwise duplicate every testid and break Playwright strict-mode locators.
 * The drawer passes '-mobile', matching the convention the header cluster used.
 *
 * `onNavigate` lets the drawer close itself on selection; the rail omits it.
 */
export function NavList({
  nav,
  idSuffix = '',
  onNavigate,
}: {
  nav: NavContext;
  idSuffix?: string;
  onNavigate?: () => void;
}): JSX.Element {
  const location = useLocation();
  const [helpExpanded, setHelpExpanded] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { backLinks, itemsToRender, helpItem, contextTitle } = nav;
  useEffect(() => {
    const active = scrollerRef.current?.querySelector('[aria-current="page"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [location.pathname, location.search, itemsToRender]);
  const headings = sectionHeadings(itemsToRender);

  const backLinkClass = cn(
    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs',
    'text-muted-foreground hover:bg-accent hover:text-foreground',
  );

  // A sub-link is active only when the path AND any query string both match -
  // the Help tabs differ only by ?tab=.
  const isChildActive = (to: string): boolean => {
    const [childPath, childSearch] = to.split('?');
    return (
      location.pathname === childPath &&
      (childSearch ? location.search === `?${childSearch}` : true)
    );
  };

  const renderChildren = (children: NonNullable<NavContext['items'][number]['children']>): JSX.Element => (
    <div className="ml-7 mt-0.5 flex flex-col gap-0.5">
      {children.map((child) => (
        <Link
          key={child.testId}
          to={child.to}
          data-testid={`${child.testId}${idSuffix}`}
          onClick={onNavigate}
          className={cn(
            'rounded-md px-3 py-1.5 text-xs',
            isChildActive(child.to)
              ? 'bg-accent font-medium text-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {child.label}
        </Link>
      ))}
    </div>
  );

  return (
    <>
      {backLinks.length > 0 && (
        <div className="mb-2 flex flex-col gap-0.5">
          {backLinks.map(({ to, label, testId, Icon }) => (
            <Link
              key={testId}
              to={to}
              data-testid={`${testId}${idSuffix}`}
              onClick={onNavigate}
              className={backLinkClass}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {label}
            </Link>
          ))}
        </div>
      )}

      <div ref={scrollerRef} className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {contextTitle !== null && (
          /* The context is a PERSON, so the rail names them. Not a role word — see
             NavContext.contextTitle. */
          /* idSuffix like every other id here: without it the rail's copy and the mobile
             drawer's copy answer to the same testid, and at mobile widths a locator finds
             the HIDDEN rail one first. Caught by the tablet and mobile viewports; the
             desktop sweep cannot see it. */
          <li
            className="px-3 pb-1 pt-2 text-sm font-semibold tracking-tight"
            data-testid={`nav-context-title${idSuffix}`}
          >
            {contextTitle}
          </li>
        )}
        {/* Keyed by testId, never `to` — items may share a destination (both /cases),
            and duplicate keys duplicated the rail (FB-0026). */}
        {itemsToRender.map(({ to, label, Icon, testId, end, children }, idx) => {
          const heading = headings[idx];
          const itemClass = (active: boolean): string =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm',
              active
                ? 'bg-accent font-medium text-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            );
          // A destination with a query string ("/cases?lens=work") is one of several doors
          // onto one path, and NavLink matches the PATH — every such door would light up at
          // once, and each would set aria-current. Same rule as the Help sub-links.
          const searchTo = to.includes('?');
          return (
            <div key={testId}>
              {heading != null && (
                <p className="px-3 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {heading}
                </p>
              )}

              {searchTo ? (
                <Link
                  to={to}
                  data-testid={`${testId}${idSuffix}`}
                  onClick={onNavigate}
                  aria-current={isChildActive(to) ? 'page' : undefined}
                  className={itemClass(isChildActive(to))}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {label}
                </Link>
              ) : (
                <NavLink
                  to={to}
                  end={end}
                  data-testid={`${testId}${idSuffix}`}
                  onClick={onNavigate}
                  className={({ isActive }) => itemClass(isActive)}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {label}
                </NavLink>
              )}

              {children && children.length > 0 && renderChildren(children)}
            </div>
          );
        })}
      </div>

      {/* Pinned at bottom; Help collapsed by default, + to expand. The privacy notice
          is here for the same reason Help is: outside useNavContext's scope switch, so
          one entry reaches the global nav and all four context navs on both surfaces.
          As a page footer it read as a cut-off page (human testing, 2026-08-19). */}
      <div className="mt-2 border-t border-border pt-2">
        {helpItem && (
          <>
            <button
              type="button"
              data-testid={`${helpItem.testId}${idSuffix}`}
              aria-expanded={helpExpanded}
              onClick={() => setHelpExpanded((v) => !v)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm',
                'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <helpItem.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="flex-1 text-left">{helpItem.label}</span>
              <span className="text-xs" aria-hidden="true">{helpExpanded ? '−' : '+'}</span>
            </button>
            {helpExpanded && helpItem.children && helpItem.children.length > 0
              ? renderChildren(helpItem.children)
              : null}
          </>
        )}

        <NavLink
          to="/privacy"
          data-testid={`nav-privacy${idSuffix}`}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm',
              isActive
                ? 'bg-accent font-medium text-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          <Shield className="h-4 w-4 shrink-0" aria-hidden="true" />
          Privacy notice
        </NavLink>
      </div>
    </>
  );
}
